import { ImageLoaderProps } from 'next/image';

const CLOUD_NAME = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME || 'unur';
const DEFAULT_MAX_WIDTH = 1440;
const PRODUCT_CARD_MAX_WIDTH = 960;
const PRODUCT_CARD_TRANSFORMS = (width: number) =>
  `c_fill,g_center,ar_5:6,w_${width}/f_auto,q_auto:good,fl_progressive:semi`;

export type ImageLoaderPreset = 'default' | 'product-card';

/**
 * Image loader that routes BigCommerce CDN images through Cloudinary fetch.
 *
 * Cloudinary acts as the delivery CDN for all product images, providing:
 * - f_auto: WebP/AVIF format negotiation based on browser Accept header
 * - q_auto:good: quality optimisation matching other Cloudinary presets
 * - w_{width}: exact width matching Next.js srcset requests
 *
 * The BC CDN URL (with {:size} resolved) is the fetch source — Cloudinary
 * downloads it once, transforms, caches at edge, and serves subsequent
 * requests from cache.
 */
interface ExtendedImageLoaderProps extends ImageLoaderProps {
  preset?: ImageLoaderPreset;
}

export default function bcCdnImageLoader({
  src,
  width,
  preset = 'default',
}: ExtendedImageLoaderProps): string {
  const maxWidth = preset === 'product-card' ? PRODUCT_CARD_MAX_WIDTH : DEFAULT_MAX_WIDTH;
  const w = Math.min(width, maxWidth);

  // Cloudinary upload URLs — inject width transformation
  if (src.includes('res.cloudinary.com/') && src.includes('/image/upload/')) {
    if (preset === 'product-card') {
      return src.replace('/image/upload/', `/image/upload/${PRODUCT_CARD_TRANSFORMS(w)}/`);
    }

    return src.replace('/image/upload/', `/image/upload/w_${w}/f_auto,q_auto:good,fl_progressive:semi/`);
  }

  // Other Cloudinary URLs (e.g. pre-transformed hero images) — pass through
  if (src.includes('res.cloudinary.com')) return src;

  // Anything that isn't a remote http(s) URL — static imports, /_next/static
  // assets, data URIs, SVGs bundled at build time — pass through. Next.js
  // resolves these to CDN-served static paths that don't need a loader.
  if (!src.startsWith('http://') && !src.startsWith('https://')) return src;

  // Other allow-listed remotes (e.g. storage.googleapis.com) — pass through
  // without wrapping. These aren't BC CDN URLs and don't need Cloudinary
  // transformation.
  if (!src.includes('bigcommerce.com')) return src;

  // BigCommerce CDN URLs — proxy through Cloudinary /image/fetch/
  const bcUrl = src.replace('{:size}', `${w}w`);

  if (preset === 'product-card') {
    return `https://res.cloudinary.com/${CLOUD_NAME}/image/fetch/${PRODUCT_CARD_TRANSFORMS(w)}/${encodeURIComponent(bcUrl)}`;
  }

  return `https://res.cloudinary.com/${CLOUD_NAME}/image/fetch/w_${w}/f_auto,q_auto:good,fl_progressive:semi/${encodeURIComponent(bcUrl)}`;
}
